/**
 * AI:
 * Status: "in progress"
 * Type: Component
 * SubType: WizardStep
 * Reason: Step 2 of POC wizard - select and order logic blocks, then create
 */
import {Component, EventEmitter, OnInit, OnDestroy, Output} from '@angular/core';
import {Subscription} from 'rxjs';
import {LgPocWizardStateService} from '../../../../service/domain/lg-poc-wizard-state/lg-poc-wizard-state.service';
import {
    LgPocCalculationMappingAssistantService
} from '../../../../service/api/lg-poc-calculation-mapping-assistant.service';
import {LgPocSelectedLogic} from '../../../../interface/lg-poc-wizard-state.interface';
import {LgPocBulkCreateRequestDto} from '../../../../model/dto/api/lg-poc-bulk-create-request.dto';
import {MvsCoreService} from '../../../../../../core/shared/service/mvs-core.service';
import {ObjectRequestList} from '../../../../../../core/shared/dto/object/request/object-request-list';

interface LogicOption {
    label: string;
    value: number;
}

@Component({
    selector: 'lg-poc-wizard-step-mapping',
    templateUrl: './lg-poc-wizard-step-mapping.component.html',
    styleUrls: ['./lg-poc-wizard-step-mapping.component.scss'],
    standalone: false
})
export class LgPocWizardStepMappingComponent implements OnInit, OnDestroy {

    @Output() onFinish = new EventEmitter<void>();
    @Output() onGoBack = new EventEmitter<void>();

    logicList: LogicOption[] = [];
    selectedLogics: LgPocSelectedLogic[] = [];
    selectedLogicToAdd: number;

    loadingLogics: boolean = false;
    creating: boolean = false;
    error: string;

    private subscriptions: Subscription[] = [];

    constructor(
        private wizardState: LgPocWizardStateService,
        private mappingAssistantService: LgPocCalculationMappingAssistantService,
        private coreService: MvsCoreService
    ) {
    }

    ngOnInit(): void {
        this.loadLogics();

        // Restore selected logics if going back
        const state = this.wizardState.state;
        if (state.selectedLogics?.length > 0) {
            this.selectedLogics = [...state.selectedLogics];
        }
    }

    loadLogics(): void {
        this.loadingLogics = true;
        const objectRequest = ObjectRequestList.createBasic(false, [], []);

        const crudService = this.coreService.getCrudService('lg.Logic');
        const sub = crudService.list(objectRequest).subscribe({
            next: (result: any) => {
                this.logicList = result.entries?.map((entry: any) => ({
                    label: entry.name || entry.internalName,
                    value: entry.id
                })) || [];
                this.loadingLogics = false;
            },
            error: () => {
                this.loadingLogics = false;
            }
        });
        this.subscriptions.push(sub);
    }

    addLogic(): void {
        if (!this.selectedLogicToAdd) return;

        const alreadySelected = this.selectedLogics.some(s => s.logicId === this.selectedLogicToAdd);
        if (alreadySelected) return;

        const logicOption = this.logicList.find(l => l.value === this.selectedLogicToAdd);
        if (!logicOption) return;

        this.selectedLogics.push({
            logicId: logicOption.value,
            logicName: logicOption.label,
            orderPriority: this.selectedLogics.length + 1
        });
        this.selectedLogicToAdd = undefined;
    }

    removeLogic(index: number): void {
        this.selectedLogics.splice(index, 1);
        this.reorderLogics();
    }

    moveUp(index: number): void {
        if (index <= 0) return;
        const temp = this.selectedLogics[index];
        this.selectedLogics[index] = this.selectedLogics[index - 1];
        this.selectedLogics[index - 1] = temp;
        this.reorderLogics();
    }

    moveDown(index: number): void {
        if (index >= this.selectedLogics.length - 1) return;
        const temp = this.selectedLogics[index];
        this.selectedLogics[index] = this.selectedLogics[index + 1];
        this.selectedLogics[index + 1] = temp;
        this.reorderLogics();
    }

    private reorderLogics(): void {
        this.selectedLogics.forEach((logic, i) => {
            logic.orderPriority = i + 1;
        });
    }

    get isValid(): boolean {
        return this.selectedLogics.length > 0;
    }

    get availableLogics(): LogicOption[] {
        const selectedIds = new Set(this.selectedLogics.map(s => s.logicId));
        return this.logicList.filter(l => !selectedIds.has(l.value));
    }

    onCreate(): void {
        if (!this.isValid || this.creating) return;

        this.creating = true;
        this.error = undefined;

        const state = this.wizardState.state;
        const request: LgPocBulkCreateRequestDto = {
            name: state.name,
            alias: state.alias,
            logicGroupId: state.logicGroupId,
            description: state.description,
            steps: this.selectedLogics.map(logic => ({
                logicId: logic.logicId,
                orderPriority: logic.orderPriority
            }))
        };

        const sub = this.mappingAssistantService.create(request).subscribe({
            next: (result) => {
                this.creating = false;
                this.wizardState.completeStep2({
                    selectedLogics: [...this.selectedLogics],
                    typeId: result.typeId,
                    typeName: result.typeName
                });
            },
            error: (err) => {
                this.creating = false;
                this.error = err?.error?.message || 'Erstellung fehlgeschlagen.';
            }
        });
        this.subscriptions.push(sub);
    }

    onBack(): void {
        this.wizardState.goBackToStep1();
        this.onGoBack.emit();
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(s => s.unsubscribe());
    }
}
